import Link from "next/link";
import { IntegrationIconLarge } from "@/components/ui/IntegrationIcons";
import { getIntegrationColor } from "@/lib/composio-tools";
import type { SkillCardProps, CreatorInfo } from "./types";
import { COMPOSIO_CDN } from "./types";
import {
  LibraryByline,
  isEmoji,
  formatCount,
} from "@/components/library/chrome";
import { isSvgIcon, SvgSkillIcon } from "@/lib/svg-skill-icons";
import { isIconUrl } from "@/lib/abilities/icon";
import type { SkillSummary, PublicSkillSummary } from "@bash/shared";

// =============================================================================
// Pastel Palette & Letter Helpers (used by both SkillIcon and glow color)
// =============================================================================

/** Palette: [lightBg, darkBg, lightText, darkText] — uses design system 18-color palette */
const PASTEL_PALETTE: [string, string, string, string][] = [
  ["rgba(224,62,62,0.12)", "rgba(224,62,62,0.15)", "#e03e3e", "#e03e3e"], // red
  ["rgba(203,145,47,0.12)", "rgba(203,145,47,0.15)", "#cb912f", "#cb912f"], // amber
  ["rgba(68,131,97,0.12)", "rgba(68,131,97,0.15)", "#448361", "#448361"], // green
  ["rgba(37,99,160,0.12)", "rgba(37,99,160,0.15)", "#2563a0", "#2563a0"], // blue
  ["rgba(105,64,165,0.12)", "rgba(105,64,165,0.15)", "#6940a5", "#6940a5"], // purple
  ["rgba(173,26,114,0.12)", "rgba(173,26,114,0.15)", "#ad1a72", "#ad1a72"], // pink
  ["rgba(15,123,108,0.12)", "rgba(15,123,108,0.15)", "#0f7b6c", "#0f7b6c"], // teal
  ["rgba(62,83,160,0.12)", "rgba(62,83,160,0.15)", "#3e53a0", "#3e53a0"], // indigo
];

function getLetterStyle(slug: string): {
  bg: string;
  darkBg: string;
  color: string;
  darkColor: string;
} {
  let hash = 0;
  for (const ch of slug) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  const [bg, darkBg, color, darkColor] =
    PASTEL_PALETTE[Math.abs(hash) % PASTEL_PALETTE.length];
  return { bg, darkBg, color, darkColor };
}

// =============================================================================
// Display Name — prettify slugs when name is missing or equals the slug
// =============================================================================

/** Convert "color-palette" → "Color Palette" */
function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getDisplayName(skill: SkillSummary | PublicSkillSummary): string {
  const name = skill.name || skill.slug;
  // If name looks like a slug (has hyphens, no spaces), prettify it
  if (name.includes("-") && !name.includes(" ")) {
    return slugToTitle(name);
  }
  return name;
}

// =============================================================================
// Color Utilities
// =============================================================================

export function getSkillBrandColor(
  skill: SkillSummary | PublicSkillSummary
): string {
  if (skill.icon?.startsWith(COMPOSIO_CDN)) {
    const slug = skill.icon.replace(COMPOSIO_CDN, "");
    return getIntegrationColor(slug);
  }
  // For SVG and letter-avatar skills, use the pastel color
  if (isSvgIcon(skill.icon) || !isEmoji(skill.icon)) {
    return getLetterStyle(skill.slug).color;
  }
  return getIntegrationColor(skill.slug);
}

// =============================================================================
// Provider Detection
// =============================================================================

const ANTHROPIC_SKILL_SLUGS = new Set([
  "pdf",
  "pptx",
  "docx",
  "xlsx",
  "document-skills-pdf",
  "document-skills-pptx",
  "document-skills-docx",
  "document-skills-xlsx",
  "algorithmic-art",
  "brand-guidelines",
  "canvas-design",
  "doc-coauthoring",
  "frontend-design",
  "internal-comms",
  "mcp-builder",
  "slack-gif-creator",
  "theme-factory",
  "web-artifacts-builder",
  "webapp-testing",
]);

type ProviderType = "Anthropic" | "Composio" | "Bash" | "User";

function getProvider(
  skill: SkillSummary | PublicSkillSummary,
  creatorInfo?: CreatorInfo
): ProviderType {
  if (ANTHROPIC_SKILL_SLUGS.has(skill.slug.toLowerCase())) return "Anthropic";
  if (skill.icon?.startsWith(COMPOSIO_CDN)) return "Composio";
  // Only call it 'User' when the skill is genuinely user-scoped. System skills
  // (default-install or admin-uploaded) belong to Bash even when creatorInfo
  // is passed for the current user. Without the scope check, every installed
  // skill rendered with the user's name as the provider.
  const scope = (skill as SkillSummary).scope;
  if (scope === "user" && creatorInfo) return "User";
  return "Bash";
}

// =============================================================================
// SkillIcon — pastel first-letter avatars for user-created skills
// =============================================================================

export function SkillIcon({
  skill,
  size,
}: {
  skill: SkillSummary | PublicSkillSummary;
  size: number;
}) {
  if (skill.icon?.startsWith(COMPOSIO_CDN)) {
    const slug = skill.icon.replace(COMPOSIO_CDN, "");
    return <IntegrationIconLarge integration={slug} size={size} />;
  }

  // Brand-image icon (e.g. '/brand/higgsfield.jpg' on the ability detail
  // hero) — a covered photo tile: the art fills the chip, takes its rounding,
  // and wears the shared hairline ring instead of falling to a letter avatar.
  if (isIconUrl(skill.icon)) {
    return (
      <span
        className="inline-flex overflow-hidden rounded-16 flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={skill.icon} alt="" className="ab-chip-ico__photo" />
      </span>
    );
  }

  // SVG icon — e.g. "svg:skills", "svg:agents", "svg:routines"
  if (isSvgIcon(skill.icon)) {
    const { bg, darkBg, color, darkColor } = getLetterStyle(skill.slug);
    return (
      <span
        className="inline-flex items-center justify-center rounded-16 flex-shrink-0 letter-avatar"
        style={{
          width: size,
          height: size,
          ["--la-bg" as string]: bg,
          ["--la-bg-dark" as string]: darkBg,
          ["--la-color" as string]: color,
          ["--la-color-dark" as string]: darkColor,
          background: `var(--la-bg)`,
          color: `var(--la-color)`,
        }}
      >
        <SvgSkillIcon icon={skill.icon!} size={size * 0.5} />
      </span>
    );
  }

  // Emoji icon — render directly
  if (isEmoji(skill.icon)) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-16 flex-shrink-0 bg-white/80 dark:bg-white/[0.12] border border-black/[0.04] dark:border-white/[0.12] leading-none"
        style={{
          width: size,
          height: size,
          fontSize: size * 0.5,
        }}
      >
        {skill.icon}
      </span>
    );
  }

  // Pastel first-letter avatar
  const displayName = getDisplayName(skill);
  const letter = displayName.charAt(0).toUpperCase();
  const { bg, darkBg, color, darkColor } = getLetterStyle(skill.slug);

  return (
    <span
      className="inline-flex items-center justify-center rounded-16 flex-shrink-0 letter-avatar font-bold leading-none tracking-[-0.02em]"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        ["--la-bg" as string]: bg,
        ["--la-bg-dark" as string]: darkBg,
        ["--la-color" as string]: color,
        ["--la-color-dark" as string]: darkColor,
        background: `var(--la-bg)`,
        color: `var(--la-color)`,
      }}
    >
      {letter}
    </span>
  );
}

// =============================================================================
// SkillCard — the house card species (abd-*), with the library's own foot
// =============================================================================

export function SkillCard({
  skill,
  isInstalled = false,
  creatorInfo,
  onSelect,
  onInstall,
  onRemove,
  installing = false,
  removing = false,
}: SkillCardProps) {
  const provider = getProvider(skill, creatorInfo);
  const isPublicSkill = "downloadCount" in skill;
  const installs = isPublicSkill
    ? (skill as PublicSkillSummary).downloadCount
    : 0;
  const displayName = getDisplayName(skill);
  const authorName = isPublicSkill
    ? (skill as PublicSkillSummary).authorName
    : undefined;
  const authorAvatarUrl = isPublicSkill
    ? (skill as PublicSkillSummary).authorAvatarUrl
    : undefined;
  const byName =
    authorName || (provider === "User" ? creatorInfo?.name || "You" : provider);
  const byAvatar = authorName
    ? authorAvatarUrl
    : provider === "User"
    ? creatorInfo?.avatar
    : undefined;

  return (
    <Link
      href={`/skills/${skill.slug}`}
      prefetch={false}
      className="abd-card relative flex-1 min-w-0 text-left font-sans text-[inherit] no-underline bg-bg-elevated border border-border-default rounded-20 p-4 sm:pt-6 sm:px-6.5 sm:pb-4.5 cursor-pointer overflow-hidden flex flex-col transition-colors after:[content:''] after:absolute after:-top-22.5 after:-right-22.5 after:w-85 after:h-70 after:bg-gold-glow after:pointer-events-none hover:border-gold-line-34"
      onClick={onSelect}
    >
      <div className="abd-card__top flex items-center gap-3.5">
        <span className="abd-card__chip w-10 h-10 rounded-12 bg-gold-molten-wash border border-solid border-gold-molten-mid-16 flex items-center justify-center shrink-0">
          <SkillIcon skill={skill} size={19} />
        </span>
        <span className="abd-card__name text-4 font-medium tracking-[-0.01em] text-fg-default [overflow-wrap:anywhere]">
          {displayName}
        </span>
      </div>
      <p className="abd-card__desc text-3.5 leading-[1.65] text-fg-subtle mt-3.5 flex-1 line-clamp-2 ">
        {skill.description}
      </p>
      <div className="abd-card__foot flex items-center justify-between gap-2.5 mt-4 pt-3.5 border-t border-border-subtle">
        <LibraryByline name={byName} avatar={byAvatar} />
        <span className="lib-card__actions inline-flex items-center gap-2.5 shrink-0 min-h-6.5">
          {isPublicSkill && installs > 0 && (
            <span className="lib-card__by inline-flex items-center gap-2 min-w-0 font-mono text-3 tracking-wider text-fg-subtle whitespace-nowrap overflow-hidden text-ellipsis">
              {formatCount(installs)} installs
            </span>
          )}
          {isInstalled && (
            <span className="lib-card__state inline-flex items-center gap-2 shrink-0 font-mono text-3 tracking-wider text-gold-ink">
              <i className="lib-dot w-1.5 h-1.5 rounded-full shrink-0 bg-gold-dot animate-lib-breathe motion-reduce:animate-none" />
              installed
            </span>
          )}
          {isInstalled && onRemove && (
            <button
              className="lib-card__remove font-sans text-3 font-medium text-fg-subtle bg-bg-elevated border border-border-default rounded-100 py-1.5 px-3.5 shrink-0 cursor-pointer transition-[color,border-color,opacity] duration-150 ease-out hover:text-red hover:border-red-line-40 disabled:opacity-56 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              disabled={removing}
            >
              {removing ? "Removing" : "Remove"}
            </button>
          )}
          {!isInstalled && onInstall && (
            <button
              className="lib-card__install font-sans text-3 font-medium text-fg-muted bg-bg-elevated border border-border-default rounded-100 py-1.5 px-3.5 shrink-0 cursor-pointer transition-colors hover:text-gold-ink hover:border-gold-line-40 disabled:opacity-56 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onInstall();
              }}
              disabled={installing}
            >
              {installing ? "Installing" : "Install"}
            </button>
          )}
        </span>
      </div>
    </Link>
  );
}
